// Choice sets for TP-Scout-Requests, mirroring the backend enums
// (users.ScoutRequest.POSITION_CHOICES / CATEGORY_CHOICES / FOOT_CHOICES). Labels
// follow the design copy (British spelling: "Centre-back", "Left winger"); the
// values are the exact keys the API stores and filters on.

export const SCOUT_POSITIONS = [
  { value: "goal_keeper", label: "Goalkeeper" },
  { value: "center_back", label: "Centre-back" },
  { value: "left_back", label: "Left-back" },
  { value: "right_back", label: "Right-back" },
  { value: "center_midfielder", label: "Central midfielder" },
  { value: "defensive_midfielder", label: "Defensive midfielder" },
  { value: "attacking_midfielder", label: "Attacking midfielder" },
  { value: "left_winger", label: "Left winger" },
  { value: "right_winger", label: "Right winger" },
  { value: "center_forward", label: "Centre-forward" },
];

// Normalise any position token to a comparable form: lowercase, US "center" →
// British "centre", and separators (space/underscore/hyphen) collapsed. This lets
// a single map catch BOTH the enum KEYS (e.g. "center_back") and the raw display
// snapshots the backend persists for wyscout picks (e.g. "Centre-Back").
const normPos = (s) =>
  String(s).toLowerCase().replace(/center/g, "centre").replace(/[\s_-]+/g, " ").trim();

const NORM_TO_LABEL = {};
SCOUT_POSITIONS.forEach((p) => {
  NORM_TO_LABEL[normPos(p.value)] = p.label;
  NORM_TO_LABEL[normPos(p.label)] = p.label;
});

// Any stored position token (enum key OR raw display snapshot) → the ONE canonical
// design spelling ("Centre-back"). Falls back to a de-slugged Title Case form so an
// unknown token still renders sensibly. Applied on every surface that shows a
// position so spellings never diverge (M4-FE N-6 / M2 N-3 label sweep).
export const formatScoutPosition = (value) => {
  if (!value) return "";
  const hit = NORM_TO_LABEL[normPos(value)];
  if (hit) return hit;
  return String(value).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export const SCOUT_CATEGORIES = [
  { value: "professional", label: "Professional" },
  { value: "youth", label: "Youth" },
];

// Preferred-foot segmented control (design 1a/2a): Left / Right / Either.
export const SCOUT_FEET = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "either", label: "Either" },
];

// Design T6 label palette (name + colour). The colour keys are the backend enum
// (ScoutAgencyLabel.COLOR_CHOICES); the hex is the FE mapping.
export const SCOUT_LABEL_COLORS = {
  red: "#DC2626",
  amber: "#D97706",
  green: "#059669",
  blue: "#2283FF",
  purple: "#7C3AED",
};

// The order the swatch row shows in the New-label composer (design 6a).
export const SCOUT_LABEL_COLOR_ORDER = ["red", "amber", "green", "blue", "purple"];

// Agency-internal request priority (design 5b/6a). Value 1|2|3 mirrors the backend
// ScoutRequestAgencyMark.priority; null clears it. Each carries the design's flag
// tint (light bg + strong same-hue text) and the short verb the menu shows.
export const SCOUT_PRIORITIES = [
  { value: 1, label: "Prio 1", verb: "act now", bg: "#FEE2E2", color: "#DC2626", dot: "#DC2626" },
  { value: 2, label: "Prio 2", verb: "this week", bg: "#FEF3C7", color: "#D97706", dot: "#D97706" },
  { value: 3, label: "Prio 3", verb: "later", bg: "#F3F4F6", color: "#6B7280", dot: "#6B7280" },
];

export const priorityMeta = (value) => SCOUT_PRIORITIES.find((p) => p.value === value) || null;
